import React, { memo } from 'react';
import classNames from 'classnames';
import { Icon } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import { LANGUAGE_CN } from '@home/_config/config';
import { Collapse } from '@/pages/aigc/components/Collapse';
import { isEmpty } from 'lodash-es';
import { useIntl } from 'umi';
import EllipsisHover from '@/pages/aigc/components/EllipsisHover';
import AnswerLoading from '../../../pages/Agent/Copilot/components/AnswerLoading';
import { AccordionItemContent } from './AccordionItemContent';
import { useStreamSticky } from '../hooks/useStreamSticky';
import type { ISwitchAgentAccordionGroup } from '../index';

export interface IAccordionItemWrapperProps {
  /** 分组ID */
  id: string;
  /** 分组数据 */
  group: ISwitchAgentAccordionGroup;
  /** 显示序号（从1开始） */
  displayIndex: number;
  /** 展开的ID列表 */
  expandedIds: string[];
  /** 展开状态引用 */
  expandStateRef: React.MutableRefObject<{ cancel?: string[]; manual: string[]; }>;
  /** 是否加载中 */
  isLoading?: boolean;
  /** 内容区域自定义类名 */
  contentClassName?: string;
  /** 展开状态图标类型 */
  iconTypeOpen?: string;
  /** 收起状态图标类型 */
  iconTypeClose?: string;
  /** 处理折叠分组的方法 */
  handleCollapseGroup: (id: string) => void;
  /** 流式输出状态 */
  streamingState?: string;
  /** 添加到手动列表的方法 */
  addToManualList: (id: string) => void;
  /** 是否启用吸顶功能，默认 true */
  enableSticky?: boolean;
  /** 滚动容器类名，可选参数 */
  scrollContainerClassName?: string;
  /**
   * 文案最大显示行数（吸顶 / 非吸顶均生效），超出显示 "..." 并 hover 出气泡展示完整文案
   * 不传 / 0 表示不限制
   * 推荐值：PC=5、Copilot=10、移动端=15
   */
  stickyMaxLines?: number;
  /** 消息 ID */
  messageId?: string;
}

const AccordionItemWrapperInner = ({
  id,
  group,
  displayIndex,
  expandedIds,
  expandStateRef,
  isLoading,
  streamingState,
  contentClassName,
  iconTypeOpen,
  iconTypeClose,
  handleCollapseGroup,
  addToManualList,
  enableSticky = true,
  scrollContainerClassName,
  stickyMaxLines = 0,
  messageId,
}: IAccordionItemWrapperProps) => {
  const { locale } = useIntl();
  const { header, items } = group;
  const isOpen = expandedIds.includes(id);
  const isManualOpen = expandStateRef.current?.manual.includes(id);
  const planLoading = isLoading && !header.done && isEmpty(items);

  // 每个 item 都有独立的吸顶 Hook，传入配置对象
  const { isSticky, placeholderHeight, headerRef, containerRef, scrollToContainer } = useStreamSticky({
    id,
    isActive: isOpen && enableSticky,
    offset: 0,
    scrollContainerClassName,
  });

  /**
   * 处理头部点击事件
   * 收起/展开内容，如果是吸顶状态下收起，则滚动到容器位置
   */
  const handleHeaderClick = () => {
    handleCollapseGroup(id);
    // 吸顶状态下点击收起后，滚动到容器位置
    if (isSticky) {
      scrollToContainer();
    }
  };

  // 输出区文案：序号 + Agent 名 + （可选）输入文案
  const timeSep = locale === LANGUAGE_CN ? '：' : ': ';
  const headPrefix = `${displayIndex}. ${header.new_agent}${header.input ? timeSep : ''}`;
  const headInput = header.input || '';
  const fullText = `${headPrefix}${headInput}`;
  // 始终按 stickyMaxLines 限制行数（不仅吸顶）；超出 hover 出气泡查看完整内容
  // 这样吸顶/非吸顶下 header 高度始终一致，避免 placeholder 跳变
  const useEllipsis = stickyMaxLines > 0;

  const outputContent = useEllipsis ? (
    <EllipsisHover
      className="dba-system-message-mode-output"
      text={fullText}
      maxLines={stickyMaxLines}
      placement="left"
      overlayClassName="dba-switch-agent-accordion-bubble dba-nav-menu-overlay"
    >
      <span className="dba-system-message-tools">{headPrefix}</span>
      <span className="dba-system-message-agent">{headInput}</span>
    </EllipsisHover>
  ) : (
    <div className="dba-system-message-mode-output">
      <span className="dba-system-message-tools">{headPrefix}</span>
      <span className="dba-system-message-agent">{headInput}</span>
    </div>
  );

  return (
    <div ref={containerRef} className="dba-switch-agent-accordion-item">
      {/* 占位元素：当吸顶时，保持原位置不变，防止内容跳动 */}
      {isSticky && <div style={{ height: `${placeholderHeight}px` }} />}
      <div
        ref={headerRef}
        className={classNames(
          "dba-system-message",
          "dba-system-message-search",
          { "dba-system-message-search-sticky": isSticky }
        )}
      >
        <div className="dba-system-message-status-icon">
          {isLoading && !header.done && streamingState === 'pending' ? (
            <Icon type="loading" className="dba-system-loading" />
          ) : (
            <SvgIcon type="agent-true" />
          )}
        </div>
        <div
          className="dba-system-message-icon-container"
          onClick={handleHeaderClick}
        >
          {outputContent}
          <div className="dba-system-message-collapse">
            <SvgIcon type={isOpen ? iconTypeOpen : iconTypeClose} />
          </div>
        </div>
      </div>
      <Collapse isOpen={isOpen}>
        <div
          className={classNames(
            'dba-switch-agent-accordion-content',
            contentClassName,
          )}
        >
          {planLoading ? (
            <AnswerLoading
              className={'dba-switch-agent-accordion-content-loading'}
            />
          ) : (
            !isEmpty(items) && (
              <AccordionItemContent
                items={items}
                isOpen={isOpen}
                isManualOpen={isManualOpen}
                isDone={header.done ?? false}
                streamingState={streamingState}
                thinkId={id}
                messageId={messageId}
                addToManualList={addToManualList}
              />
            )
          )}
        </div>
      </Collapse>
    </div>
  );
};

export const AccordionItemWrapper = memo(AccordionItemWrapperInner);
AccordionItemWrapper.displayName = 'AccordionItemWrapper';
